import React, { useState, useEffect } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useQuery } from '@tanstack/react-query';
import { Branch, getBranches } from 'api/branchApi';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  colors,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

interface Props {
  label: string;
  onChange: (data: Branch[]) => void;
  defaultSelected?: Branch[];
}

const MultiBranchAutoCompleteForFilter = ({
  label,
  onChange,
  defaultSelected,
}: Props) => {
  const { data: branches, isFetching } = useQuery(['branches'], getBranches);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Branch[]>([]);

  useEffect(() => {
    if (defaultSelected && defaultSelected.length > 0 && branches) {
      const selectedData = branches?.filter((branch) =>
        defaultSelected.includes(branch)
      );

      setSelected(selectedData);
    } else {
      setSelected([]);
    }
  }, [defaultSelected, branches]);

  return (
    <>
      <Autocomplete
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={isFetching ? [] : branches}
        loading={open && isFetching}
        getOptionLabel={(option) => option?.name || ''}
        value={selected}
        renderOption={(option) => (
          <span>
            <Box display="block">
              <Typography variant="body1">{option.name}</Typography>
            </Box>
          </span>
        )}
        renderTags={(value: Branch[], getTagProps) =>
          value.map((option: Branch, index: number) => (
            <Chip
              sx={{ marginRight: '0.4rem' }}
              variant="outlined"
              label={option.name}
              {...getTagProps({ index })}
            />
          ))
        }
        getOptionSelected={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {open && isFetching && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        onChange={(_, data: Branch[]) => {
          onChange && onChange(data.map((u) => u));
        }}
        PaperComponent={(paperProps) => {
          const { children, ...restPaperProps } = paperProps;
          return (
            <Paper {...restPaperProps}>
              {branches?.length > 0 && (
                <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
                  <FormControlLabel
                    onClick={(e) => {
                      e.preventDefault();
                      onChange && onChange(branches?.map((u) => u));
                      setOpen(false);
                    }}
                    label=""
                    control={
                      <Box m={1}>
                        <Button
                          id="select-all-checkbox"
                          variant="outlined"
                          size="small"
                          sx={{
                            color: colors.grey[600],
                            borderColor: colors.grey[600],
                          }}>
                          Select all
                        </Button>
                      </Box>
                    }
                  />
                </Box>
              )}
              <Divider />
              {children}
            </Paper>
          );
        }}
      />
    </>
  );
};

export default MultiBranchAutoCompleteForFilter;
